import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Search, Trash2, Pencil, AlertTriangle, Minus, Plus as PlusIcon, QrCode, ScanLine, Download, Loader2, Link2, Warehouse, HardHat, Building2, Boxes, MapPin, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BarcodeScanner from "@/components/BarcodeScanner";
import { MovimentiMagazzino } from "@/components/MovimentiMagazzino";
import { History } from "lucide-react";
import { IdentificaCodice } from "@/components/IdentificaCodice";
import { CaricoRapido } from "@/components/CaricoRapido";
import { VerificaBanner, VerificaDialog, daVerificare } from "@/components/VerificaCantiere";

const empty = { nome: "", categoria: "", unita_misura: "pz", quantita: 0, prezzo_unitario: 0, soglia_minima: 0, note: "", codice: "", ubicazione: "", cantiere_id: "" };

const UBICAZIONI = [
  { id: "generale",         label: "Inventario Generale", icon: Boxes,     filter: null },
  { id: "deposito",         label: "Deposito Centrale",   icon: Warehouse, filter: "deposito" },
  { id: "cantiere",         label: "Cantieri",            icon: HardHat,   filter: "cantiere" },
  { id: "ufficio",          label: "Ufficio",             icon: Building2, filter: "ufficio" },
];
const UBI_LABEL = { deposito: "Deposito Centrale", cantiere: "Cantiere", ufficio: "Ufficio" };
const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Magazzino() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [query, setQuery] = useState("");
  const [locTab, setLocTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "generale");  // generale | deposito | cantiere | ufficio
  const [verifica, setVerifica] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [movOpen, setMovOpen] = useState(false);
  const [mov, setMov] = useState({ id: null, delta: 0, causale: "manuale", note: "" });
  // QR labels generator
  const [qrOpen, setQrOpen] = useState(false);
  const [qrForm, setQrForm] = useState({ quantita: 100, prefisso: "ATT-", start: 1 });
  const [qrBusy, setQrBusy] = useState(false);
  // Scanner
  const [scanOpen, setScanOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null); // {code, item}
  const [associateOpen, setAssociateOpen] = useState(false); // modal "associa a nuovo"
  const [associateCode, setAssociateCode] = useState("");
  const [associateExisting, setAssociateExisting] = useState(""); // id esistente da collegare
  const [carico, setCarico] = useState({ qty: "1", prezzo: "", ubicazione: "deposito", cantiere_id: "" });
  const [suggerimento, setSuggerimento] = useState(null);
  const [aiMatch, setAiMatch] = useState(null);
  const [identLoading, setIdentLoading] = useState(false);
  const parseQty = (v) => Math.max(0, Number(String(v).replace(",", ".")) || 0);
  const applyIdent = (d) => {
    setIdentLoading(false);
    setSuggerimento(d?.riconosciuto ? d.suggerimento : null);
    const m = d?.match && d.match.confidenza >= 0.75 ? d.match : null;
    setAiMatch(m);
    if (m) setAssociateExisting((prev) => prev || m.id);
  };
  // Quick-set ubicazione modal (per materiali "non classificati")
  const [ubiOpen, setUbiOpen] = useState(false);
  const [ubiForm, setUbiForm] = useState({ id: null, ubicazione: "deposito", cantiere_id: "" });

  const load = async () => {
    const { data } = await api.get("/magazzino", { params: query ? { q: query } : {} });
    setList(data);
  };
  useEffect(() => { load(); }, [query]);
  useEffect(() => { api.get("/cantieri").then(({ data }) => setCantieri(data)).catch(() => setCantieri([])); }, []);
  const cmap = useMemo(() => Object.fromEntries(cantieri.map((c) => [c.id, c.nome])), [cantieri]);

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Inserisci il nome");
    try {
      const payload = { ...form, quantita: Number(form.quantita || 0), prezzo_unitario: Number(form.prezzo_unitario || 0), soglia_minima: Number(form.soglia_minima || 0) };
      if (editId) await api.put(`/magazzino/${editId}`, payload);
      else await api.post("/magazzino", payload);
      toast.success(editId ? "Materiale aggiornato" : "Materiale aggiunto");
      setForm(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo materiale?")) return;
    try { await api.delete(`/magazzino/${id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };

  const saveMov = async () => {
    try {
      await api.post(`/magazzino/${mov.id}/movimento`, { delta: Number(mov.delta), causale: mov.causale, note: mov.note });
      toast.success("Movimento registrato");
      setMovOpen(false); setMov({ id: null, delta: 0, causale: "manuale", note: "" });
      setScanResult(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const openEdit = (m) => { setEditId(m.id); setForm({ ...empty, ...m }); setOpen(true); };

  // Quick stock from scanner card
  const quickStock = async (itemId, delta, causale) => {
    try {
      await api.post(`/magazzino/${itemId}/movimento`, { delta, causale, note: "da scanner" });
      toast.success(`${delta > 0 ? "+" : ""}${delta} ${causale}`);
      setScanResult(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Generate QR Labels PDF
  const generaEtichette = async () => {
    const qty = Math.max(1, Math.min(2000, Number(qrForm.quantita) || 0));
    if (!qty) return toast.error("Inserisci una quantità valida (1-2000)");
    setQrBusy(true);
    try {
      const resp = await api.post("/magazzino/qr-labels",
        { quantita: qty, prefisso: qrForm.prefisso || "", start: Number(qrForm.start) || 1 },
        { responseType: "blob", timeout: 120000 }
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const start = Number(qrForm.start) || 1;
      a.download = `etichette_qr_${qrForm.prefisso || "MAT"}_${start}-${start + qty - 1}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${qty} etichette QR generate`);
      setQrOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione PDF");
    } finally { setQrBusy(false); }
  };

  // On barcode/QR detected
  const onCodeDetected = async (code) => {
    const c = (code || "").trim();
    if (!c) return;
    setScanOpen(false);
    try {
      const { data } = await api.get(`/magazzino/by-code/${encodeURIComponent(c)}`);
      setScanResult({ code: c, item: data });
    } catch (e) {
      if (e?.response?.status === 404) {
        setAssociateCode(c);
        setAssociateExisting("");
        setCarico({ qty: "1", prezzo: "", ubicazione: "deposito", cantiere_id: "" });
        setIdentLoading(true);
        setSuggerimento(null);
        setAiMatch(null);
        setAssociateOpen(true);
      } else {
        toast.error(e?.response?.data?.detail || "Errore lettura codice");
      }
    }
  };

  const assocSel = list.find((m) => m.id === associateExisting);
  useEffect(() => {
    if (!associateOpen) return;
    setCarico((c) => ({ ...c, prezzo: assocSel?.prezzo_unitario ? String(assocSel.prezzo_unitario) : "", ubicazione: assocSel?.ubicazione || "deposito", cantiere_id: assocSel?.cantiere_id || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associateExisting, associateOpen]);

  const associaCodice = async () => {
    const qty = parseQty(carico.qty);
    const prezzo = parseQty(carico.prezzo);
    if (carico.ubicazione === "cantiere" && !carico.cantiere_id) return toast.error("Seleziona il cantiere di destinazione");
    const dest = { ubicazione: carico.ubicazione, cantiere_id: carico.ubicazione === "cantiere" ? carico.cantiere_id : "" };
    if (!associateExisting) {
      // Crea nuovo articolo e associa
      setForm({ ...empty, nome: suggerimento?.nome || "", categoria: suggerimento?.categoria || "", unita_misura: suggerimento?.unita_misura || empty.unita_misura, codice: associateCode, quantita: qty, prezzo_unitario: prezzo, ...dest });
      setEditId(null);
      setAssociateOpen(false);
      setOpen(true);
      return;
    }
    const sel = assocSel;
    if (sel?.codice && sel.codice !== associateCode && !window.confirm(`"${sel.nome}" ha già il codice ${sel.codice}. Sostituirlo con ${associateCode}? La vecchia etichetta non funzionerà più.`)) return;
    try {
      await api.post(`/magazzino/${associateExisting}/associa-codice`, { codice: associateCode, carico: qty, prezzo_unitario: prezzo > 0 ? prezzo : null, ...dest });
      toast.success(qty > 0 ? `Codice associato · caricati ${qty}` : "Codice associato");
      setAssociateOpen(false);
      setAssociateCode(""); setAssociateExisting("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Filtra per tab ubicazione
  const filtered = useMemo(() => {
    if (locTab === "generale") return list;
    return list.filter((m) => (m.ubicazione || "") === locTab);
  }, [list, locTab]);

  // Conteggi per badge sui tab
  const counts = useMemo(() => ({
    generale: list.length,
    deposito: list.filter((m) => m.ubicazione === "deposito").length,
    cantiere: list.filter((m) => m.ubicazione === "cantiere").length,
    ufficio:  list.filter((m) => m.ubicazione === "ufficio").length,
    non_classificato: list.filter((m) => !m.ubicazione).length,
  }), [list]);

  const totale = useMemo(() => filtered.reduce((s, m) => s + Number(m.quantita || 0) * Number(m.prezzo_unitario || 0), 0), [filtered]);
  const sottoSoglia = useMemo(() => filtered.filter((m) => Number(m.soglia_minima || 0) > 0 && Number(m.quantita || 0) <= Number(m.soglia_minima || 0)), [filtered]);

  // Quick-set ubicazione (dalla riga tabella "Non classificato")
  const openUbiFor = (m) => {
    setUbiForm({ id: m.id, ubicazione: m.ubicazione || "deposito", cantiere_id: m.cantiere_id || "" });
    setUbiOpen(true);
  };
  const saveUbicazione = async () => {
    const u = ubiForm.ubicazione;
    if (u === "cantiere" && !ubiForm.cantiere_id) return toast.error("Seleziona il cantiere");
    try {
      await api.patch(`/magazzino/${ubiForm.id}/ubicazione`, { ubicazione: u, cantiere_id: ubiForm.cantiere_id });
      toast.success("Ubicazione aggiornata");
      setUbiOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Scorte</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Package className="h-7 w-7 text-primary" />Magazzino</h1>
          <p className="text-muted-foreground mt-1">Gestisci materiali, scansiona QR/Barcode e stampa etichette.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="scan-btn" variant="outline" className="h-11 border-primary/60 bg-primary/5 hover:bg-primary/10"
                  onClick={() => setScanOpen(true)}>
            <ScanLine className="h-4 w-4 mr-2 text-primary" /> Scansiona
          </Button>
          <Button data-testid="qr-labels-btn" variant="outline" className="h-11" onClick={() => setQrOpen(true)}>
            <QrCode className="h-4 w-4 mr-2" /> Etichette QR
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-materiale" className="h-11"><Plus className="h-4 w-4 mr-2" /> Nuovo materiale</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Modifica materiale" : "Nuovo materiale"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label><Input data-testid="m-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                <div className="col-span-2"><Label>Codice QR/Barcode</Label>
                  <div className="flex gap-2">
                    <Input data-testid="m-codice" value={form.codice || ""} placeholder="es. ATT-001 o EAN-13"
                           onChange={(e) => setForm({ ...form, codice: e.target.value })} />
                    <Button type="button" variant="outline" onClick={() => setScanOpen(true)} className="shrink-0">
                      <ScanLine className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div><Label>Categoria</Label><Input data-testid="m-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} /></div>
                <div><Label>Unità misura</Label><Input data-testid="m-unita" value={form.unita_misura} onChange={(e) => setForm({ ...form, unita_misura: e.target.value })} /></div>
                <div><Label>Quantità</Label><Input data-testid="m-quantita" type="number" step="0.01" value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} /></div>
                <div><Label>Prezzo unitario €</Label><Input type="number" step="0.01" value={form.prezzo_unitario} onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} /></div>
                <div><Label>Soglia minima</Label><Input type="number" step="0.01" value={form.soglia_minima} onChange={(e) => setForm({ ...form, soglia_minima: e.target.value })} /></div>
                <div><Label>Ubicazione</Label>
                  <Select value={form.ubicazione || "__none__"} onValueChange={(v) => setForm({ ...form, ubicazione: v === "__none__" ? "" : v, cantiere_id: v === "cantiere" ? form.cantiere_id : "" })}>
                    <SelectTrigger data-testid="m-ubicazione"><SelectValue placeholder="— non classificato —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— non classificato —</SelectItem>
                      <SelectItem value="deposito">Deposito Centrale</SelectItem>
                      <SelectItem value="cantiere">Cantiere</SelectItem>
                      <SelectItem value="ufficio">Ufficio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.ubicazione === "cantiere" && (
                  <div className="col-span-2">
                    <Label>Cantiere di destinazione</Label>
                    <Select value={form.cantiere_id || ""} onValueChange={(v) => setForm({ ...form, cantiere_id: v })}>
                      <SelectTrigger data-testid="m-cantiere"><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                      <SelectContent>
                        {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              </div>
              <Button data-testid="m-save" onClick={save} className="h-11 mt-2">{editId ? "Aggiorna" : "Salva"}</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Articoli {locTab !== "generale" && <span className="normal-case text-[9px] opacity-70">({UBI_LABEL[locTab]})</span>}</div><div className="text-2xl font-extrabold">{filtered.length}</div></CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valore totale</div><div className="text-2xl font-extrabold text-primary">{fmt(totale)}</div></CardContent></Card>
        <Card className={`card-elevated ${sottoSoglia.length > 0 ? "border-amber-500" : ""}`}><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sotto soglia</div><div className="text-2xl font-extrabold text-amber-600 flex items-center gap-2">{sottoSoglia.length}{sottoSoglia.length > 0 && <AlertTriangle className="h-5 w-5" />}</div></CardContent></Card>
      </div>

      {/* Tabs ubicazione (stile capsule Presenze) */}
      <Tabs value={locTab} onValueChange={setLocTab}>
        <TabsList>
          {UBICAZIONI.map((u) => {
            const Icon = u.icon;
            const n = counts[u.filter || "generale"];
            return (
              <TabsTrigger key={u.id} value={u.filter || "generale"} data-testid={`loc-tab-${u.id}`}>
                <Icon className="h-4 w-4 mr-2" />
                {u.label}
                {n > 0 && <span className="ml-2 text-[10px] opacity-70 font-mono">({n})</span>}
              </TabsTrigger>
            );
          })}
          <TabsTrigger value="movimenti" data-testid="loc-tab-movimenti"><History className="h-4 w-4 mr-2" /> Movimenti</TabsTrigger>
        </TabsList>
      </Tabs>

      {locTab === "movimenti" && <MovimentiMagazzino />}

      {locTab === "cantiere" && <VerificaBanner count={filtered.filter(daVerificare).length} />}

      <Card className={`card-elevated ${locTab === "movimenti" ? "hidden" : ""}`}><CardContent className="p-4 flex gap-2 items-center">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input data-testid="m-search" placeholder="Cerca materiale..." value={query} onChange={(e) => setQuery(e.target.value)} className="h-9" />
      </CardContent></Card>

      <Card className={`card-elevated ${locTab === "movimenti" ? "hidden" : ""}`}><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Nome</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Codice</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Categoria</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Ubicazione</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">UM</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Quantità</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">€/UM</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Valore</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => {
              const low = Number(m.soglia_minima || 0) > 0 && Number(m.quantita || 0) <= Number(m.soglia_minima || 0);
              const ubi = m.ubicazione || "";
              const ubiLabel = ubi === "cantiere"
                ? (cmap[m.cantiere_id] ? `Cantiere · ${cmap[m.cantiere_id]}` : "Cantiere (non assegnato)")
                : (UBI_LABEL[ubi] || "");
              return (
                <tr key={m.id} className={`border-t border-border ${low ? "bg-amber-50 dark:bg-amber-900/10" : ""}`} data-testid={`row-mat-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.nome}{low && <span className="ml-2 text-[10px] text-amber-700 font-bold">⚠ sotto soglia</span>}</td>
                  <td className="px-4 py-3 text-[11px] font-mono text-muted-foreground">{m.codice || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.categoria || "—"}</td>
                  <td className="px-4 py-3">
                    {ubi ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                        <MapPin className="h-3 w-3 text-primary" />
                        {ubiLabel}
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openUbiFor(m)}
                        data-testid={`set-ubi-${m.id}`}
                        className="h-7 px-2 text-[10px] border-dashed border-primary/40 text-primary hover:bg-primary/10"
                      >
                        <MapPin className="h-3 w-3 mr-1" />
                        Assegna ubicazione
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3">{m.unita_misura}</td>
                  <td className="px-4 py-3 tabular-nums font-bold">{m.quantita}</td>
                  <td className="px-4 py-3 tabular-nums">{fmt(m.prezzo_unitario)}</td>
                  <td className="px-4 py-3 tabular-nums text-primary">{fmt(Number(m.quantita || 0) * Number(m.prezzo_unitario || 0))}</td>
                  <td className="px-4 py-3 flex gap-1 justify-end">
                    {ubi === "cantiere" && (
                      <Button variant="ghost" size="icon" onClick={() => setVerifica(m)} title="Verifica giacenza" data-testid={`verifica-${m.id}`}>
                        <ClipboardCheck className={`h-3 w-3 ${daVerificare(m) ? "text-amber-600" : ""}`} />
                      </Button>
                    )}
                    {ubi && (
                      <Button variant="ghost" size="icon" onClick={() => openUbiFor(m)} title="Cambia ubicazione" data-testid={`ubi-${m.id}`}>
                        <MapPin className="h-3 w-3" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => { setMov({ id: m.id, delta: -1, causale: "scarico", note: "" }); setMovOpen(true); }} title="Scarica" data-testid={`mov-out-${m.id}`}><Minus className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setMov({ id: m.id, delta: 1, causale: "carico", note: "" }); setMovOpen(true); }} title="Carica" data-testid={`mov-in-${m.id}`}><PlusIcon className="h-3 w-3 text-primary" /></Button>
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => openEdit(m)}><Pencil className="h-3 w-3" /></Button>}
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => remove(m.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
              {locTab === "generale" ? "Nessun materiale. Aggiungi il primo." : `Nessun materiale in "${UBI_LABEL[locTab] || locTab}". Puoi assegnare l'ubicazione dai materiali in "Inventario Generale".`}
            </td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Movimento manuale */}
      <Dialog open={movOpen} onOpenChange={setMovOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Movimento magazzino</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Quantità (±)</Label><Input data-testid="mov-delta" type="number" step="0.01" value={mov.delta} onChange={(e) => setMov({ ...mov, delta: e.target.value })} /></div>
            <div><Label>Causale</Label><Input value={mov.causale} onChange={(e) => setMov({ ...mov, causale: e.target.value })} /></div>
            <div><Label>Note</Label><Input value={mov.note} onChange={(e) => setMov({ ...mov, note: e.target.value })} /></div>
          </div>
          <Button data-testid="mov-save" onClick={saveMov} className="h-11 mt-2">Registra movimento</Button>
        </DialogContent>
      </Dialog>

      {/* Modal Genera etichette QR */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-primary" /> Genera etichette QR</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Verrà generato un PDF A4 (40 etichette/pagina) con codici progressivi pronti per la stampa su fogli adesivi.
            </div>
            <div>
              <Label>Prefisso (opzionale)</Label>
              <Input data-testid="qr-prefisso" value={qrForm.prefisso}
                     placeholder="es. ATT- oppure MAT-"
                     onChange={(e) => setQrForm({ ...qrForm, prefisso: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantità</Label>
                <Input data-testid="qr-quantita" type="number" min="1" max="2000"
                       value={qrForm.quantita}
                       onChange={(e) => setQrForm({ ...qrForm, quantita: e.target.value })} />
              </div>
              <div>
                <Label>Da numero</Label>
                <Input data-testid="qr-start" type="number" min="1"
                       value={qrForm.start}
                       onChange={(e) => setQrForm({ ...qrForm, start: e.target.value })} />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground bg-secondary rounded p-2">
              Esempio primi codici: <strong>{(qrForm.prefisso || "") + String(qrForm.start || 1).padStart(3, "0")}</strong>,{" "}
              {(qrForm.prefisso || "") + String((Number(qrForm.start) || 1) + 1).padStart(3, "0")}, …
            </div>
            <Button data-testid="qr-generate" onClick={generaEtichette} disabled={qrBusy} className="w-full h-11">
              {qrBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Scarica PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scanner */}
      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onCodeDetected} />

      {/* Scheda articolo trovato dallo scanner */}
      <Dialog open={!!scanResult} onOpenChange={(o) => !o && setScanResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" /> Articolo scansionato</DialogTitle></DialogHeader>
          {scanResult && (
            <div className="space-y-3">
              <div className="rounded-lg bg-secondary p-3">
                <div className="text-[11px] font-mono text-muted-foreground">{scanResult.code}</div>
                <div className="text-lg font-extrabold">{scanResult.item.nome}</div>
                <div className="text-xs text-muted-foreground">{scanResult.item.categoria || "—"}</div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Giacenza</div>
                    <div className="text-2xl font-extrabold tabular-nums">{scanResult.item.quantita} <span className="text-sm font-normal text-muted-foreground">{scanResult.item.unita_misura}</span></div>
                  </div>
                  {Number(scanResult.item.soglia_minima || 0) > 0 && Number(scanResult.item.quantita || 0) <= Number(scanResult.item.soglia_minima || 0) && (
                    <span className="text-[11px] font-bold text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> sotto soglia</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="scan-carico" onClick={() => quickStock(scanResult.item.id, 1, "carico")} className="h-14 flex-col gap-0">
                  <PlusIcon className="h-5 w-5" /> <span className="text-xs">Carico +1</span>
                </Button>
                <Button data-testid="scan-scarico" variant="outline" onClick={() => quickStock(scanResult.item.id, -1, "scarico")} className="h-14 flex-col gap-0">
                  <Minus className="h-5 w-5" /> <span className="text-xs">Scarico −1</span>
                </Button>
              </div>
              <Button
                data-testid="scan-custom"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  setMov({ id: scanResult.item.id, delta: 1, causale: "carico", note: `scanner ${scanResult.code}` });
                  setScanResult(null); setMovOpen(true);
                }}
              >
                Movimento personalizzato…
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Codice sconosciuto → associa */}
      <Dialog open={associateOpen} onOpenChange={setAssociateOpen}>        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Codice non riconosciuto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded bg-secondary p-2 text-sm">
              Codice letto: <strong className="font-mono">{associateCode}</strong>
            </div>
            {associateOpen && <IdentificaCodice code={associateCode} onApply={applyIdent} />}
            <div className="text-xs text-muted-foreground">Associa questo codice a:</div>
            <div>
              <Label>Articolo esistente</Label>
              <select
                data-testid="assoc-existing"
                value={associateExisting}
                onChange={(e) => setAssociateExisting(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">— oppure crea nuovo articolo —</option>
                {aiMatch && !list.some((m) => m.id === aiMatch.id) && <option value={aiMatch.id}>{aiMatch.nome} (riconosciuto)</option>}
                {list.map((m) => (
                  <option key={m.id} value={m.id}>{m.nome}{m.codice ? ` (già: ${m.codice})` : ""}</option>
                ))}
              </select>
            </div>
            {associateOpen && (
              <CaricoRapido
                value={carico}
                onChange={setCarico}
                codice={associateCode}
                nome={assocSel?.nome || (associateExisting ? aiMatch?.nome : suggerimento?.nome) || ""}
                unita={assocSel?.unita_misura || (!associateExisting && suggerimento?.unita_misura) || ""}
                cantieri={cantieri}
              />
            )}
            <Button data-testid="assoc-save" onClick={associaCodice} disabled={identLoading} className="w-full h-11">
              {identLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {associateExisting ? (parseQty(carico.qty) > 0 ? `Associa e carica ${carico.qty}` : "Associa a articolo esistente") : suggerimento ? `Crea "${suggerimento.nome}"` : "Crea nuovo articolo con questo codice"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <VerificaDialog item={verifica} onClose={() => setVerifica(null)} onDone={load} />

      {/* Modal quick-set ubicazione */}
      <Dialog open={ubiOpen} onOpenChange={setUbiOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Assegna ubicazione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ubicazione</Label>
              <Select value={ubiForm.ubicazione} onValueChange={(v) => setUbiForm({ ...ubiForm, ubicazione: v, cantiere_id: v === "cantiere" ? ubiForm.cantiere_id : "" })}>
                <SelectTrigger data-testid="ubi-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deposito">Deposito Centrale</SelectItem>
                  <SelectItem value="cantiere">Cantiere</SelectItem>
                  <SelectItem value="ufficio">Ufficio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ubiForm.ubicazione === "cantiere" && (
              <div>
                <Label>Cantiere</Label>
                <Select value={ubiForm.cantiere_id} onValueChange={(v) => setUbiForm({ ...ubiForm, cantiere_id: v })}>
                  <SelectTrigger data-testid="ubi-cantiere"><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                  <SelectContent>
                    {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="ubi-save" onClick={saveUbicazione} className="w-full h-11">Salva ubicazione</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
